import { CenteredModal } from "@verse_core/shared_components/CenteredModal";
import { IconButton } from "@verse_core/shared_components/buttons/IconButton";
import { StandardButton } from "@verse_core/shared_components/buttons/StandardButton";

export const WHAT_TO_EXPECT_FROM_ORDER_TITLE =
  "What to expect from your order?";

/** Patient order expectations steps. */
const ORDER_EXPECTATION_STEPS = [
  {
    title: "Confirm the supplies",
    description: "We'll confirm your order and follow up if we need anything.",
  },
  {
    title: "We create a customized package for you",
    description:
      "We match you with the right products based on sizing, availability, and coverage.",
  },
  {
    title: "Track your delivery",
    description:
      "You'll receive real-time text updates so you always know where your order is.",
  },
  {
    title: "We bill your insurance",
    description:
      "We handle insurance billing directly, so you pay as little as possible.",
  },
] as const;

interface Props {
  hideModal: () => void;
}

/** Patient order expectations modal. */
export function WhatToExpectFromOrderModal({ hideModal }: Props) {
  return (
    <CenteredModal
      className="tw-max-h-[min(90vh,800px)] tw-w-[calc(100vw-32px)] tw-max-w-[540px] tw-overflow-hidden tw-rounded-lg tw-bg-white tw-shadow-[0px_1px_8px_0px_rgba(0,0,0,0.25)]"
      hideModal={hideModal}
    >
      <div
        className="tw-flex tw-max-h-[min(90vh,800px)] tw-flex-col"
        data-testid="what-to-expect-from-order-modal"
      >
        <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-[10px] tw-border-0 tw-border-b tw-border-solid tw-border-gray-light-1 tw-px-[24px] tw-py-[16px]">
          <h2 className="tw-m-0 tw-flex-1 tw-font-degular tw-text-[24px] tw-font-medium tw-leading-none tw-tracking-[-0.24px] tw-text-blue-dark-1">
            {WHAT_TO_EXPECT_FROM_ORDER_TITLE}
          </h2>
          <IconButton
            className="tw-flex tw-h-[15px] tw-w-[15px] tw-shrink-0 tw-items-center tw-justify-center tw-text-[20px] tw-text-gray-4"
            icon="fas fa-xmark"
            onClick={hideModal}
          />
        </div>
        <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-[15px] tw-overflow-y-auto tw-p-[24px]">
          <div className="tw-flex tw-flex-col tw-gap-[10px]">
            <p className="tw-m-0 tw-font-dm-sans tw-text-[14px] tw-font-medium tw-leading-normal tw-text-gray-2">
              Your provider has partnered with Verse Medical to get you the
              supplies you need, quickly and easily. Here&apos;s what happens
              next.
            </p>
            <div className="tw-rounded-lg tw-bg-gray-light-3 tw-px-[32px] tw-py-[16px]">
              <ol className="tw-mt-[1px] tw-list-none tw-p-0">
                {ORDER_EXPECTATION_STEPS.map((step, index) => {
                  const isLast = index === ORDER_EXPECTATION_STEPS.length - 1;
                  return (
                    <li
                      key={step.title}
                      className={`tw-flex tw-gap-[10px] tw-pb-[3px] tw-pr-[3px] ${isLast ? "" : "tw-mb-[8px]"}`}
                    >
                      <div className="tw-relative tw-flex tw-w-[12px] tw-shrink-0 tw-justify-center tw-self-stretch">
                        <div className="tw-pointer-events-none tw-relative tw-z-[1] tw-flex tw-h-[12px] tw-w-[12px] tw-shrink-0 tw-items-center tw-justify-center">
                          <span
                            className="tw-block tw-h-[12px] tw-w-[12px] tw-shrink-0 tw-rounded-full tw-bg-blue-2 tw-ring-2 tw-ring-gray-light-3"
                            aria-hidden
                          />
                        </div>
                        {!isLast && (
                          <div
                            className="tw-pointer-events-none tw-absolute -tw-bottom-[8px] tw-left-1/2 tw-top-[12px] tw-z-0 tw-w-px -tw-translate-x-1/2 tw-bg-gray-5"
                            aria-hidden
                          />
                        )}
                      </div>
                      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[3px]">
                        <p className="tw-m-0 tw-font-dm-sans tw-text-[14px] tw-font-bold tw-leading-none tw-text-gray-0">
                          {step.title}
                        </p>
                        <p className="tw-m-0 tw-font-dm-sans tw-text-[12px] tw-font-medium tw-leading-normal tw-text-gray-3">
                          {step.description}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
            <p className="tw-m-0 tw-font-dm-sans tw-text-[14px] tw-font-medium tw-leading-normal tw-text-gray-2">
              You&apos;re in good hands — we&apos;ll keep you updated every step
              of the way.
            </p>
          </div>
          <StandardButton
            style="primary-blue"
            className="tw-h-[36px] tw-w-full"
            onClick={hideModal}
            dataTestId="what-to-expect-from-order-got-it"
          >
            Got it
          </StandardButton>
        </div>
      </div>
    </CenteredModal>
  );
}
